import { useDisclosure } from '@mantine/hooks';
import { Box, Button, Divider, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import { useGetDeviceGroups, useGetDeviceSpecsByIDs, useGetInstances } from 'gen/client';
import { InstanceDTO } from 'gen/model';
import { useEffect, useState } from 'react';

import CustomNoRowsOverlay from '@ui/Components/DataGrid/CustomNoRowsOverlay';
import InfoButton from '@ui/Components/Icons/InfoButton';
import QRCodeModal from '@ui/Components/Modals/QRCodeModal';
import { useIsNewDevice, useRenkiConfig } from '@ui/Utils/device/installationMetadata';

import DownloadAppsModal from '@/Components/Layout/Appbar.tsx/DownloadAppsModal';
import ViewContainer from '@/Components/Layout/ViewContainer';

import DeviceListItem from './DeviceListItem';
import PlatformModal from './PlatformModal';

const QrUrl = 'http://portal.localhost/device?addMobile=true';

export default function DeviceListView() {
    const { data: instances = [] } = useGetInstances();
    const { data: deviceGroups = [] } = useGetDeviceGroups();
    const deviceSpecIds = deviceGroups
        .filter((item) => item.devicespec_id)
        .map((i) => i.devicespec_id)
        .join(',');

    const tauri = isTauri();
    const { isNewDevice } = useIsNewDevice();
    const theme = useTheme();

    const { data: multiDeviceSpecs } = useGetDeviceSpecsByIDs(deviceSpecIds, {
        swr: {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    });

    const { config } = useRenkiConfig();

    // Sort device groups to put current device first
    const sortedDeviceGroups = [...deviceGroups].sort((a, b) => {
        const aIsCurrent = tauri && config?.installationId === a.installation_id;
        const bIsCurrent = tauri && config?.installationId === b.installation_id;

        if (aIsCurrent && !bIsCurrent) {
            return -1;
        }
        if (!aIsCurrent && bIsCurrent) {
            return 1;
        }
        return 0;
    });

    const [platformModal, { close: handlePlatformModalClose }] = useDisclosure(false);

    const [qrModalComputer, { close: handleQrModalComputerClose }] = useDisclosure(false);

    const [qrModalHandheld, { close: handleQrModalHandheldClose }] = useDisclosure(false);

    const [downloadAppsModal, { close: closeDownloadAppsModal, open: openDownloadAppsModal }] = useDisclosure(false);

    const selectedInstance =
        instances.find((instance) => instance.id === config?.favoriteInstance) ?? instances[0] ?? null;

    return (
        <ViewContainer sx={{ py: { sm: 2, xs: 1.5 }, width: '100%' }}>
            <PlatformModal onClose={handlePlatformModalClose} open={platformModal} />
            <QRCodeModal handleClose={handleQrModalComputerClose} open={qrModalComputer} url={QrUrl} />
            <QRCodeModal handleClose={handleQrModalHandheldClose} open={qrModalHandheld} url={QrUrl} />
            <DownloadAppsModal onClose={closeDownloadAppsModal} open={downloadAppsModal} />
            <Stack sx={{ flex: 1, height: '100%', width: '100%' }}>
                <Stack spacing={0.5} sx={{ mb: 1.5, px: { md: 0, xs: 0.5 } }}>
                    <Typography sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 0.2 }} variant="h5">
                        Devices
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ color: 'text.secondary', fontWeight: 500 }} variant="body2">
                            Manage devices linked to your account
                        </Typography>
                        <InfoButton title="View, add and manage the devices associated with your account across places." />
                    </Stack>
                </Stack>
                <Paper
                    elevation={0}
                    sx={{
                        backdropFilter: 'blur(4px)',
                        background: alpha(theme.palette.primary.main, 0.03),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        borderRadius: 3,
                        mb: 2,
                        p: { sm: 1.25, xs: 1 },
                    }}
                >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            {isNewDevice ? (
                                <InfoButton title="Add a new device to your account. If you're using our app, it can help automatically detect info." />
                            ) : null}
                        </Stack>
                        <Button
                            disabled={Boolean(
                                tauri &&
                                config?.installationId &&
                                deviceGroups.some((g) => g.installation_id === config.installationId),
                            )}
                            onClick={() => {
                                window.dispatchEvent(new Event('device-discovery:prompt'));
                            }}
                            size="small"
                            variant="outlined"
                        >
                            Add Device
                        </Button>
                    </Stack>
                </Paper>
                <Divider sx={{ mb: 1, opacity: 0.5 }} />
                {/* Content list */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: { sm: 'auto', xs: 'auto' },
                        pr: 1,
                        pt: 1,
                        width: '100%',
                    }}
                >
                    {sortedDeviceGroups.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 3,
                                pb: 6,
                                pt: 1,
                            }}
                        >
                            {sortedDeviceGroups.map((item) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        flexGrow: 0,
                                        flexShrink: 0,
                                        minWidth: { sm: 280, xs: 'auto' },
                                        width: { sm: 'calc(50% - 12px)', xs: '100%' },
                                    }}
                                >
                                    <DeviceListItem
                                        deviceGroup={item}
                                        deviceSpec={multiDeviceSpecs?.find(
                                            (i) => i.id?.toString() === item.devicespec_id?.toString(),
                                        )}
                                        selectedInstance={selectedInstance}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Stack sx={{ alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                            <Stack spacing={1} sx={{ alignItems: 'center' }}>
                                <CustomNoRowsOverlay text="No devices added." />
                                {tauri ? null : (
                                    <Button onClick={openDownloadAppsModal} variant="contained">
                                        Download App
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </ViewContainer>
    );
}
